import React, { useEffect, useState } from "react";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  deleteEvaluationProcessDefinitionAction,
  getEvaluationProcessDefinitionAction,
  getEvaluationProcessDefinitionCheckCollaboratorsAction,
} from "../actions";
import {
  getData,
  getIsLoading,
  getEvaluationTypeList,
  getFormList,
  getScoreTypeList,
  getObjectivePeriodList,
  getStatusList,
} from "../selectors";
import { openDialogAction } from "@icarius-common/dialog/actions";
import { getLocalizedString } from "@icarius-localization/strings";
import paths from "@icarius-router/paths";

const useEvaluationProcessDefinition = () => {

  const [formIsOpen, setFormIsOpen] = useState(false);
  const [duplicateDialogIsOpen, setDuplicateDialogIsOpen] = useState(false);
  const [changeStatusDialogIsOpen, setChangeStatusDialogIsOpen] = useState(false);
  const [checkCollaboratorsDialogIsOpen, setCheckCollaboratorsDialogIsOpen] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [mailDialogIsOpen, setMailDialogIsOpen] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();
  const data = useSelector(getData);
  const isLoading = useSelector(getIsLoading);
  const evaluationTypeList = useSelector(getEvaluationTypeList);
  const formList = useSelector(getFormList);
  const scoreTypeList = useSelector(getScoreTypeList);
  const objectivePeriodList = useSelector(getObjectivePeriodList);
  const statusList = useSelector(getStatusList);

  useEffect(() => {
    dispatch(getEvaluationProcessDefinitionAction());
  }, [dispatch])

  const handleDelete = (document) => {
    dispatch(deleteEvaluationProcessDefinitionAction({ internalCode: document.internalCode }));
  }

  const handleOpenDeleteDialog = (gridRef) => {
    let selectedRows = gridRef.api.getSelectedRows();
    if (selectedRows.length !== 1) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
      return;
    }

    dispatch(openDialogAction({
      title: getLocalizedString("atention"),
      msg: getLocalizedString("confirmProcessDeletion"),
      onConfirm: () => dispatch(openDialogAction({
        title: getLocalizedString("atention"),
        msg: (
          <>
            {getLocalizedString("evaluationProcessDefinitionMessage1")}
            <br />
            {getLocalizedString("evaluationProcessDefinitionMessage2")}
          </>
        ),
        onConfirm: () => handleDelete(selectedRows[0]),
      }))
    }));

  }

  const handleOpenCheckCollaboratorsDialog = (gridRef) => {
    let selectedRows = gridRef.api.getSelectedRows();
    if (selectedRows.length !== 1) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
      return;
    }

    dispatch(getEvaluationProcessDefinitionCheckCollaboratorsAction(selectedRows[0].processCode))
      .then((resp) => {
        if (resp.status === 200) {
          setSelectedData(selectedRows[0]);
          setCheckCollaboratorsDialogIsOpen(true);
        }
      });
  }

  const handleCloseCheckCollaboratorsDialog = () => {
    setCheckCollaboratorsDialogIsOpen(false);
    setSelectedData(null);
  }


  const handleOpenCreateDialog = () => {
    setSelectedData(null);
    setFormIsOpen(true);
  }

  const handleOpenEditDialog = (gridRef) => {
    let selectedRows = gridRef.api.getSelectedRows();
    if (selectedRows.length !== 1) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
      return;
    }

    setSelectedData(selectedRows[0]);
    setFormIsOpen(true);
  }

  const handleCloseCreateEditDialog = () => {
    setFormIsOpen(false);
    setSelectedData(null);
  }

  const handleOpenChangeStatusDialog = (gridRef) => {
    let selectedRows = gridRef.api.getSelectedRows();
    if (selectedRows.length !== 1) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
      return;
    }

    setSelectedData(selectedRows[0]);
    setChangeStatusDialogIsOpen(true);
  }

  const handleCloseChangeStatusDialog = () => {
    setChangeStatusDialogIsOpen(false);
    setSelectedData(null);
  }

  const handleOpenDuplicateDialog = (gridRef) => {
    let selectedRows = gridRef.api.getSelectedRows();
    if (selectedRows.length !== 1) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
      return;
    }

    setSelectedData(selectedRows[0]);
    setDuplicateDialogIsOpen(true);
  }

  const handleCloseDuplicateDialog = () => {
    setDuplicateDialogIsOpen(false);
    setSelectedData(null);
  }

  const handleOpenAssign = (gridRef) => {
    let selectedRows = gridRef.api.getSelectedRows();
    if (selectedRows.length !== 1) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
      return;
    }

    history.push({
      pathname: paths.evaluationProcessDefinitionAssignment,
      search: `?processCode=${selectedRows[0].processCode}&processName=${selectedRows[0].processName}&processStatusString=${selectedRows[0].evaluationStatusString}&processStatus=${selectedRows[0].evaluationStatus}`,
    });
  }

  const handleOpenMailDialog = (gridRef, statusKey) => {
    let selectedRows = gridRef.api.getSelectedRows();
    if (selectedRows.length !== 1) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
      return;
    }

    if (statusKey === 'E' && selectedRows[0].evaluationStatus !== 'E') {
      dispatch(openSnackbarAction({ msg: getLocalizedString("mailDialogEvaluationProcessDefinition2"), severity: "warning" }));
      return;
    }

    if (statusKey === 'K' && selectedRows[0].evaluationStatus !== 'K') {
      dispatch(openSnackbarAction({ msg: getLocalizedString("mailDialogEvaluationProcessDefinition"), severity: "warning" }));
      return;
    }

    setSelectedData(selectedRows[0]);
    setMailDialogIsOpen(true);
  }

  const handleCloseMailDialog = () => {
    setMailDialogIsOpen(false);
    setSelectedData(null);
  }

  const state = {
    formIsOpen,
    duplicateDialogIsOpen,
    changeStatusDialogIsOpen,
    selectedData,
    checkCollaboratorsDialogIsOpen,
    mailDialogIsOpen,
  }

  const handlers = {
    handleOpenDeleteDialog,
    handleOpenCreateDialog,
    handleOpenEditDialog,
    handleCloseCreateEditDialog,
    handleOpenDuplicateDialog,
    handleCloseDuplicateDialog,
    handleOpenChangeStatusDialog,
    handleCloseChangeStatusDialog,
    handleDelete,
    handleOpenCheckCollaboratorsDialog,
    handleCloseCheckCollaboratorsDialog,
    handleOpenAssign,
    handleOpenMailDialog,
    handleCloseMailDialog,
  }

  return {
    data,
    evaluationTypeList,
    formList,
    scoreTypeList,
    objectivePeriodList,
    statusList,
    isLoading,
    state,
    handlers,
  }
}

export default useEvaluationProcessDefinition;